import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import LanguageToggle from './LanguageToggle';

const Navigation = () => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const allLinks = [
    { to: '/', label: t.navHome, visible: true },
    { to: '/gallery', label: t.navGallery, visible: settings.page_gallery_visible },
    { to: '/about', label: t.navAbout, visible: settings.page_about_visible },
    { to: '/instruments', label: t.navInstruments, visible: settings.page_instruments_visible },
    { to: '/restorations', label: t.navRestorations, visible: settings.page_restorations_visible },
    { to: '/contact', label: t.navContact, visible: settings.page_contact_visible },
  ];

  const links = allLinks.filter(l => l.visible);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/30">
      <nav className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between" aria-label="Main navigation">
        {/* Brand */}
        <Link to="/" className="font-serif text-xl tracking-wide text-foreground hover:text-foreground/80 transition-colors">
          Szoradi Instruments
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-8">
          {links.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-xs tracking-[0.2em] uppercase font-sans transition-colors duration-200 link-underline ${
                isActive(link.to)
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <LanguageToggle />
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-3 lg:hidden">
          <LanguageToggle />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-foreground"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden border-t border-border/30 bg-background/98 backdrop-blur-sm">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                className={`text-sm tracking-[0.15em] uppercase font-sans py-2 transition-colors ${
                  isActive(link.to) ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
